import { Fragment, type ReactNode } from "react";
import { Outlet } from "react-router";
import { useApplyBrand } from "../../api/brand";
import { useLegal, type Legal } from "../../api/legal";
import { Wordmark } from "../../app/Sidebar";
import { useBrand } from "../../lib/brand";

/** The legal links that are set, quietly, under the sign-in pages: Hosterr's, then an agency's own terms on its brand. */
function LegalLinks() {
  const legal = useLegal();
  if (!legal) return null;
  const links = [
    { label: "Terms", href: legal.termsUrl },
    { label: "Privacy", href: legal.privacyUrl },
    { label: "Acceptable use", href: legal.acceptableUseUrl },
    { label: "Data processing agreement", href: legal.dpaUrl },
    { label: legal.agency ? `${legal.agency.name}'s terms` : "", href: legal.agency?.termsUrl ?? null },
  ].filter((l): l is { label: string; href: string } => !!l.href && !!l.label);
  if (!links.length) return null;
  return (
    <p className="mt-1.5 text-[12px] text-dim">
      {links.map((l, i) => (
        <Fragment key={l.label}>
          {i ? <span aria-hidden="true"> · </span> : null}
          <a className="text-dim underline-offset-2 hover:text-fg hover:underline" href={l.href} target="_blank" rel="noreferrer">
            {l.label}
          </a>
        </Fragment>
      ))}
    </p>
  );
}

/**
 * What making an account or joining agrees to, under its button: the terms, the acceptable use policy and an agency's
 * own terms, the ones that are set (a privacy policy is a notice, so it stays in the links below). The API records
 * what was shown (routes/legal.ts). Nothing set, nothing said.
 */
export function TermsAgreement({ legal, joining }: { legal: Legal | null | undefined; joining?: boolean }) {
  if (!legal) return null;
  const items = [
    legal.termsUrl ? { before: "the ", label: "Terms of service", href: legal.termsUrl } : null,
    legal.acceptableUseUrl ? { before: "the ", label: "Acceptable use policy", href: legal.acceptableUseUrl } : null,
    legal.agency ? { before: "", label: `${legal.agency.name}'s terms`, href: legal.agency.termsUrl } : null,
  ].filter((x) => !!x);
  if (!items.length) return null;
  return (
    <p className="text-center text-[12.5px] text-dim">
      By {joining ? "joining" : "creating an account"}, you agree to{" "}
      {items.map((x, i) => (
        <Fragment key={x.label}>
          {i ? (i === items.length - 1 ? " and " : ", ") : null}
          {x.before}
          <a className="link" href={x.href} target="_blank" rel="noreferrer">
            {x.label}
          </a>
        </Fragment>
      ))}
      .
    </p>
  );
}

/** The quiet frame around sign-in screens: the warm desk with a little daylight. `legal`: the legal links under it. */
export function AuthFrame({ children, legal }: { children: ReactNode; legal?: boolean }) {
  // The sign-in pages show the brand of the address they're on.
  useApplyBrand(null);
  const brand = useBrand();
  // The platform's brand carries Hosterr's address (HOSTERR_SUPPORT_EMAIL); an older API's doesn't.
  const supportEmail = brand.supportEmail ?? (brand.platform ? "hello@hosterr.com" : null);
  return (
    <div className="relative flex min-h-dvh flex-col bg-canvas">
      <div className="hx-lamp" aria-hidden="true" />
      <header className="relative z-10 flex h-20 items-center px-6 sm:px-10">
        <Wordmark />
      </header>
      <main id="main" className="relative z-10 flex flex-1 items-start justify-center px-5 pt-[6vh] pb-16 sm:items-center sm:pt-0">
        <div className="w-full max-w-[420px] animate-rise-in">{children}</div>
      </main>
      <footer className="relative z-10 px-6 pb-6 text-center sm:px-10">
        <span className="kicker">{brand.name}</span>
        {/* A way to ask for help on every brand: the agency's own, or Hosterr's address on its own brand. */}
        {supportEmail || brand.supportUrl ? (
          <p className="mt-1.5 text-[12px] text-dim">
            Need help?{" "}
            {brand.supportUrl ? (
              <a className="link" href={brand.supportUrl} target="_blank" rel="noreferrer">
                {brand.name} support
              </a>
            ) : (
              <a className="link" href={`mailto:${supportEmail}`}>
                {supportEmail}
              </a>
            )}
          </p>
        ) : null}
        {legal ? <LegalLinks /> : null}
      </footer>
    </div>
  );
}

export function AuthLayout() {
  return (
    <AuthFrame legal>
      <Outlet />
    </AuthFrame>
  );
}

export function AuthCard({ kicker, title, description, children }: { kicker?: string; title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <div className="card px-6 py-7 sm:px-8 sm:py-8">
      {kicker ? <div className="kicker mb-3">{kicker}</div> : null}
      <h1 className="display text-[28px] leading-tight">{title}</h1>
      {description ? <p className="mt-2 text-[14.5px] leading-relaxed text-body">{description}</p> : null}
      <div className="mt-6">{children}</div>
    </div>
  );
}
